import '@fontsource-variable/inter-tight';
import 'ol/ol.css';
import CssBaseline from '@mui/material/CssBaseline';
import {LocalizationProvider} from '@mui/x-date-pickers-pro';
import {AdapterLuxon} from '@mui/x-date-pickers-pro/AdapterLuxon';
import {LD_CLIENTSIDE_ID} from '@src/config.ts';
import appGlobalStyles from '@src/styles/AppGlobalStyles.jsx';
import ThemeProvider from '@src/styles/ThemeProvider.jsx';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {createHead, UnheadProvider} from '@unhead/react/client';
import {asyncWithLDProvider} from 'launchdarkly-react-client-sdk';
import {Settings} from 'luxon';
import {NuqsAdapter} from 'nuqs/adapters/react-router/v8';
import {useGeographic as setGeographic} from 'ol/proj';
import React, {StrictMode} from 'react';
import ReactDOM from 'react-dom/client';
import {
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router';
import SecureApp from './SecureApp.jsx';
import initSentry from './sentry.js';
import {checkWebGLSupport} from './utils/webgl.ts';

// Default to CRS84 coordinate system.
setGeographic();

// Default to en-US and UTC
Settings.defaultLocale = 'en-US';
Settings.defaultZone = 'utc';

const head = createHead({
  init: [
    {
      htmlAttrs: {lang: 'en'},
      title: 'Planet Insights Platform',
    },
  ],
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (
          error.response?.status === 429 ||
          (error.response?.status >= 500 && error.response?.status <= 599)
        ) {
          return failureCount < 3;
        }
        return false;
      },
    },
  },
});

// Polyfill for Windows
if (window !== undefined) {
  window.global = window;
}

// Ensure that all the blob support exists
if (Blob) {
  if (!Blob.prototype.text) {
    const reader = (blob, format) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = evt => {
          resolve(evt.target.result);
        };
        reader.onerror = () => {
          reject('Bad Blob');
        };
        if (format === 'text') {
          reader.readAsText(blob);
        } else {
          reader.readAsArrayBuffer(blob);
        }
      });
    };

    Blob.prototype.text = function () {
      return reader(this, 'text');
    };
    Blob.prototype.arrayBuffer = function () {
      return reader(this, 'arraybuffer');
    };
  }
}

// Setup Sentry
initSentry();

checkWebGLSupport();

const root = ReactDOM.createRoot(document.getElementById('root'));

const router = createBrowserRouter(
  createRoutesFromElements(<Route path="*" element={<SecureApp />} />),
  {basename: import.meta.env.BASE_URL},
);

(async () => {
  const LDProvider = await asyncWithLDProvider({
    clientSideID: LD_CLIENTSIDE_ID,
    options: {
      application: {
        id: 'acorns',
        version: import.meta.env.GIT_REV,
      },
    },
    context: {
      kind: 'user',
      key: 'user-pl-anonymous',
    },
  });

  root.render(
    <StrictMode>
      <UnheadProvider head={head}>
        <NuqsAdapter>
          <LDProvider>
            <QueryClientProvider client={queryClient}>
              <LocalizationProvider dateAdapter={AdapterLuxon}>
                <ThemeProvider>
                  <CssBaseline />
                  {appGlobalStyles}
                  <RouterProvider router={router} />
                  <ReactQueryDevtools buttonPosition="bottom-left" />
                </ThemeProvider>
              </LocalizationProvider>
            </QueryClientProvider>
          </LDProvider>
        </NuqsAdapter>
      </UnheadProvider>
    </StrictMode>,
  );
})();
